import type {
	OfflineVideoData,
	SyncOfflineViewRequest,
	TrackingState,
	ViewEngagement
} from './types';

import { STORAGE_KEYS } from '~/constants/storageKeys';

const OFFLINE_VIEWS_KEY = STORAGE_KEYS.OFFLINE_VIEWS;
const MAX_OFFLINE_VIEWS = 50;

export function getOfflineViews(): SyncOfflineViewRequest[] {
	if (!import.meta.client) return [];
	try {
		const stored = localStorage.getItem(OFFLINE_VIEWS_KEY);
		return stored ? JSON.parse(stored) : [];
	} catch {
		return [];
	}
}

export function saveOfflineViews(views: SyncOfflineViewRequest[]): void {
	if (!import.meta.client) return;
	try {
		localStorage.setItem(
			OFFLINE_VIEWS_KEY,
			JSON.stringify(views.slice(-MAX_OFFLINE_VIEWS))
		);
	} catch {
		// localStorage unavailable
	}
}

export function clearOfflineViews(): void {
	if (!import.meta.client) return;
	try {
		localStorage.removeItem(OFFLINE_VIEWS_KEY);
	} catch {
		// localStorage unavailable
	}
}

export function saveViewOffline(
	state: TrackingState,
	sessionId: string,
	engagement: Partial<ViewEngagement>,
	videoData?: OfflineVideoData
): void {
	const views = getOfflineViews();
	const existingIndex = views.findIndex(
		v => v.propertyId === state.propertyId && v.viewType === state.viewType
	);
	const existingView = existingIndex >= 0 ? views[existingIndex] : undefined;

	const offlineView: SyncOfflineViewRequest = {
		propertyId: state.propertyId,
		viewType: state.viewType,
		source: state.source,
		sessionId,
		engagement: {
			...existingView?.engagement,
			...engagement
		},
		context: { position: state.position },
		videoData: videoData ?? existingView?.videoData,
		timestamp: Date.now()
	};

	if (existingIndex >= 0) {
		views[existingIndex] = offlineView;
	} else {
		views.push(offlineView);
	}

	saveOfflineViews(views);
}

/**
 * Returns offline views that have been watched more than the threshold with video data.
 */
export function getWatchedOfflineViews(
	minCompletionRate: number = 0.5
): SyncOfflineViewRequest[] {
	return getOfflineViews()
		.filter(
			view =>
				(view.engagement?.completionRate ?? 0) >= minCompletionRate &&
				view.videoData
		)
		.sort(
			(a, b) =>
				(b.engagement?.completionRate ?? 0) -
				(a.engagement?.completionRate ?? 0)
		);
}

export function getOfflineViewsCount(): number {
	return getOfflineViews().length;
}

/**
 * Returns property IDs from offline views that have been watched more than the threshold.
 * Used to exclude these properties from featured videos when user is not logged in.
 */
export function getWatchedOfflinePropertyIds(
	minCompletionRate: number = 0.5
): string[] {
	const views = getOfflineViews();
	return views
		.filter(view => (view.engagement?.completionRate ?? 0) >= minCompletionRate)
		.map(view => view.propertyId);
}
